import React, { useState } from "react";
import { MoreHorizontal, Edit, Trash2, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import ParticipantEditModal from "@/components/participant-edit-modal";
import SetRaffleEntrancesModal from "@/components/set-raffle-entrances-modal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Performer, JamNight } from "@shared/schema";

interface ParticipantActionsDropdownProps {
  performer: Performer;
  onRemove: (performerId: number) => void;
  disabled?: boolean;
  isLoading?: boolean;
  jamNight?: JamNight;
  jamNightId?: number;
  isDragging?: boolean;
}

export default function ParticipantActionsDropdown({
  performer,
  onRemove,
  disabled = false,
  isLoading = false,
  jamNight,
  jamNightId,
  isDragging = false,
}: ParticipantActionsDropdownProps) {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [raffleEntrancesModalOpen, setRaffleEntrancesModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const updatePerformerMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: Partial<Performer>;
    }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/performers/${id}`,
        updates,
      );
      return response.json();
    },
    onSuccess: () => {
      if (jamNightId) {
        queryClient.invalidateQueries({
          queryKey: [`/api/performers?jamNightId=${jamNightId}`],
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/performers"] });
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update performer",
        variant: "destructive",
      });
    },
  });

  const handleRemove = () => {
    onRemove(performer.id);
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled || isLoading}
            className={`${isDragging 
              ? "bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400 shadow-lg" 
              : "bg-white/40 backdrop-blur-sm border-white/30 hover:bg-white/50 shadow-lg transition-all duration-300"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            style={isDragging ? {} : {
              boxShadow: '0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
            }}
          >
            <MoreHorizontal className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setEditModalOpen(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Details
          </DropdownMenuItem>
          {jamNight?.raffleEnabled && (
            <DropdownMenuItem onClick={() => setRaffleEntrancesModalOpen(true)}>
              <Ticket className="h-4 w-4 mr-2" />
              Set Raffle Entrances
            </DropdownMenuItem>
          )}
          <DropdownMenuItem 
            onClick={() => setDeleteDialogOpen(true)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Remove
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit Modal */}
      {jamNightId && (
        <ParticipantEditModal
          performer={performer}
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          jamNightId={jamNightId}
        />
      )}

      {/* Raffle Entrances Modal */}
      {jamNightId && (
        <SetRaffleEntrancesModal
          open={raffleEntrancesModalOpen}
          onOpenChange={setRaffleEntrancesModalOpen}
          performer={performer}
          jamNightId={jamNightId}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Remove Participant"
        description={`Are you sure you want to remove ${performer.name} from this event? This action cannot be undone.`}
        onConfirm={handleRemove}
        confirmText="Remove"
        isConfirming={isLoading}
      />
    </>
  );
}